/**
 * Single source of truth for "what stage is this property in?".
 *
 * The codebase has multiple parallel pipeline enums:
 *  - `User.qualifyStatus` / `User.approveStatus`
 *  - `LoanApplication.lifecycleStage` / `.status`
 *  - `Contract.contractStatus` / `.isRatified` / `.statusHistory`
 *  - `pipelineApi.currentStageKey`
 *  - localStorage `closedAt`
 *
 * `resolveStage` reads them all and produces a single 1..9 stage per the
 * unified-property spec. Refinance properties skip 4..5..6.
 */

import { USER_QUALIFY_STATUS } from '@/config/constants';
import {
  getSectionsForApplication,
  type LoanSectionKey,
} from '@/config/loanProductRegistry';
import type { ContractPipelineResponse } from '@/apis/extended/pipelineApi';
import type { Contract } from '@/types/ContractType';
import type { LoanApplication } from '@/types/LoanApplicationType';
import type { Product } from '@/types/ProductType';
import type { PropertyStage } from '@/types/PropertyView';
import type { User } from '@/types/UserType';
import { closedAtStore, toursStore } from './localStateStore';

const PURCHASE_STAGES: PropertyStage[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const REFINANCE_STAGES: PropertyStage[] = [1, 2, 3, 7, 8, 9];

export function visibleStagesFor(isRefinance: boolean): PropertyStage[] {
  return isRefinance ? REFINANCE_STAGES : PURCHASE_STAGES;
}

const PIPELINE_PROCESSING_KEYS = new Set([
  'processing',
  'title',
  'appraisal',
  'insurance',
  'survey',
  'escrow',
  'document_collection',
  'docs',
  'docs_collection',
]);

const PIPELINE_UW_KEYS = new Set([
  'underwriting',
  'uw',
  'conditional_approval',
  'clear_to_close',
  'ctc',
  'final_review',
]);

const PIPELINE_CLOSING_KEYS = new Set([
  'closing',
  'funded',
  'completed',
  'closed',
]);

const CLOSED_CONTRACT_STATUSES = new Set<Contract['contractStatus']>([
  'completed',
  'escrow',
]);

const NEGOTIATING_CONTRACT_STATUSES = new Set<Contract['contractStatus']>([
  'negotiating',
  'awaiting_signatures',
  'awaiting_buyer_signature',
  'awaiting_seller_signature',
  'repair_negotiating',
  'repair_requested',
]);

const RATIFIED_CONTRACT_STATUSES = new Set<Contract['contractStatus']>([
  'ratified',
  'active',
  'inspection_pending',
]);

const APPLICATION_FUNDED_LIFECYCLES = new Set(['ratified_purchase']);

interface ResolveContext {
  product: Product;
  application?: LoanApplication | null;
  contract?: Contract | null;
  pipeline?: ContractPipelineResponse | null;
  user?: User | null;
  isRefinance: boolean;
}

/**
 * Pure resolver. Stage rules (highest precedence first):
 *
 *  9. `closedAt` set, or pipeline says closing/funded, or contractStatus is
 *     `completed/escrow`.
 *  8. Pipeline currentStageKey in underwriting/CTC bucket.
 *  7. Pipeline currentStageKey in processing/title/appraisal bucket OR
 *     contract is ratified/active (post-acceptance) OR refinance application
 *     has been submitted/in_review.
 *  6. Contract exists and is in a negotiating-style status.
 *  5. Contract exists in draft status (offer submitted but not yet
 *     responded-to).
 *  4. Tour logged AND no contract yet (purchase only).
 *  3. Linked application exists and has any progress.
 *  2. User is qualify-status QUALIFY OR application created but no real
 *     progress yet.
 *  1. Saved.
 */
export function resolveStage(ctx: ResolveContext): PropertyStage {
  const { product, application, contract, pipeline, user, isRefinance } = ctx;
  const pipelineKey = pipeline?.data?.currentStageKey?.toLowerCase();

  const closedAt = closedAtStore.get(product._id);
  if (closedAt) return 9;
  if (pipelineKey && PIPELINE_CLOSING_KEYS.has(pipelineKey)) return 9;
  if (
    contract?.contractStatus &&
    CLOSED_CONTRACT_STATUSES.has(contract.contractStatus)
  )
    return 9;

  if (pipelineKey && PIPELINE_UW_KEYS.has(pipelineKey)) return 8;

  const applicationProgressed =
    !!application &&
    (application.status === 'submitted' ||
      application.status === 'in_review' ||
      application.status === 'approved' ||
      (application.lifecycleStage !== undefined &&
        APPLICATION_FUNDED_LIFECYCLES.has(application.lifecycleStage)));

  if (pipelineKey && PIPELINE_PROCESSING_KEYS.has(pipelineKey)) return 7;

  if (
    contract?.isRatified ||
    (contract?.contractStatus &&
      RATIFIED_CONTRACT_STATUSES.has(contract.contractStatus))
  ) {
    return 7;
  }

  // Refinance can jump straight to processing once the application is submitted
  // since stages 4-6 don't apply.
  if (isRefinance && applicationProgressed) return 7;

  if (
    contract?.contractStatus &&
    NEGOTIATING_CONTRACT_STATUSES.has(contract.contractStatus)
  ) {
    return 6;
  }

  if (contract && !isRefinance) {
    // Contract exists, no negotiation/ratification yet — buyer has submitted.
    return 5;
  }

  if (!isRefinance) {
    const tours = toursStore.list(product._id);
    if (tours.length > 0) return 4;
  }

  // Stage 3 vs 2 vs 1 — driven by application existence + completion signals.
  if (application) {
    const sectionsTotal = countApplicationSections(application);
    const sectionsComplete = countApplicationCompletedSections(application);
    const hasRealProgress =
      sectionsComplete >= 2 ||
      application.status !== 'draft' ||
      (application.borrowers && application.borrowers.length > 0);
    if (hasRealProgress) return 3;
    // Application exists but is essentially empty — Stage 2.
    return 2;
  }

  if (user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY) return 2;

  return 1;
}

function isSectionComplete(app: LoanApplication, key: LoanSectionKey): boolean {
  switch (key) {
    case 'property':
      return Boolean(app.property?.subjectPropertyAddress);
    case 'borrower':
      return Boolean(app.borrowers?.[0]?.firstName);
    case 'coBorrower':
      return Boolean(
        app.borrowers?.some((b) => b.role === 'coBorrower' && b.firstName)
      );
    case 'assets':
      return Boolean(app.assetLines && app.assetLines.length > 0);
    case 'track':
      return Boolean(app.trackRecord && app.trackRecord.length > 0);
    case 'employment':
      return Boolean(app.employment && app.employment.length > 0);
    case 'declarations':
      return Boolean(
        app.declarations && Object.keys(app.declarations).length > 0
      );
    case 'loanTerms':
      return Boolean(
        app.agencyFields?.loanAmount && app.agencyFields.loanAmount > 0
      );
    case 'liabilities':
      return Array.isArray(app.liabilityLines);
    case 'demographics':
      return Boolean(
        app.demographics && Object.keys(app.demographics).length > 0
      );
    case 'dscr':
      return Boolean(app.dscrFields && Object.keys(app.dscrFields).length > 0);
    case 'checklist': {
      const items = app.checklist || [];
      if (items.length === 0) return false;
      const required = items.filter(
        (i) => i.requiredForSubmit !== false && i.required !== false
      );
      if (required.length === 0) return true;
      return required.every((i) => i.completed);
    }
    case 'sign':
      return Boolean(app.signAttestation?.acceptedAt);
    default:
      return false;
  }
}

/** Section count from loanProductRegistry (program-aware). */
export function countApplicationSections(app: LoanApplication): number {
  return Math.max(1, getSectionsForApplication(app).length);
}

/** How many registry sections look complete for this application. */
export function countApplicationCompletedSections(
  app: LoanApplication
): number {
  const sections = getSectionsForApplication(app);
  return sections.filter((s) => isSectionComplete(app, s.key)).length;
}

/** Stage→label maps & CTAs are in `propertyCardConstants.ts`. */
